/**
 * Where the sign-in screens live, and how a page load decides which one to show.
 *
 * The screens used to have no URL at all. The email step, the per-provider screen and the
 * switch between them were React state, and the only navigation was a query parameter
 * that stripped itself on arrival while `localStorage` remembered the answer. So Back did
 * nothing: on the provider screen it reloaded the same URL, boot found the remembered
 * provider, and rendered the same screen again. The email step you had just come from was
 * reachable only through a small link, and nothing was addressable, which is why reaching
 * a given screen meant hand-editing storage. Storage was doing a URL's job and always won
 * over history.
 *
 * Two paths now carry the two persistent screens, and the URL beats storage on both:
 *
 * - `/signin` is the email step, whatever is remembered.
 * - `/signin/<provider>` is that provider's screen. Continue navigates here, which is still
 *   the full page load the one-OIDC-client-per-page-load constraint requires, and is now a
 *   history entry too, so Back returns to `/signin`.
 *
 * `?auto=1` on the provider path means "sign in straight away", and it is consumed on
 * arrival, so a page reached by Back from the identity provider shows a button rather than
 * redirecting again. Every other path decides as before: a remembered provider mounts its
 * gate quietly, and nothing remembered lands on the email step.
 *
 * A session the browser already has beats both. Tokens live in the service worker, which
 * every tab shares, so somebody signed in elsewhere who opens `/signin` should go on into
 * the app rather than be asked for an address again. Finding that session needs an OIDC
 * client, and a client is configured for one authority, so `/signin` mounts the remembered
 * provider's client and shows the email step only when it finds nobody. The remembered
 * provider is the right one to ask because it is remembered at the moment the session is
 * created and forgotten by the sign-out that ends it, so there is nothing to look for when
 * it is absent.
 *
 * Transient states stay as state. Expired, signed-out and the pending sign-out tab describe
 * the session rather than a place, and a reload should not recreate them.
 *
 * `decideSignIn` is pure so the rules can be pinned without a DOM; `main.tsx` supplies what
 * it read from the page and acts on the answer.
 */

import type { AuthProviderConfig } from '../types/api';
import { rememberRequestedAccount } from './requestedAccount';

const SEGMENT = 'signin';

/** `/signin`, under the deployment's base path. */
export function signInRootPath(base: string = import.meta.env.BASE_URL): string {
  return `${base}${SEGMENT}`;
}

/** `/signin/<provider>`, under the deployment's base path. */
export function signInProviderPath(name: string, base: string = import.meta.env.BASE_URL): string {
  return `${signInRootPath(base)}/${encodeURIComponent(name)}`;
}

/** Whether `pathname` is the email step or a provider screen. */
export function isSignInPath(pathname: string, base: string = import.meta.env.BASE_URL): boolean {
  const root = signInRootPath(base);
  return pathname === root || pathname.startsWith(`${root}/`);
}

/** The provider name a `/signin/<provider>` path carries, or null for any other path. */
export function providerNamedByPath(
  pathname: string,
  base: string = import.meta.env.BASE_URL,
): string | null {
  const prefix = `${signInRootPath(base)}/`;
  if (!pathname.startsWith(prefix)) return null;
  const rest = pathname.slice(prefix.length);
  if (!rest || rest.includes('/')) return null;
  try {
    return decodeURIComponent(rest);
  } catch {
    return null;
  }
}

/**
 * Leave for a provider's screen by a full page load, asking it to sign in at once.
 *
 * A page load rather than a re-render, and the reason is a library detail with a nasty
 * failure. `OidcProvider` is mounted without a `configurationName`, so both providers
 * share the name `default`, and @axa-fr/react-oidc resolves its client from a module-level
 * registry by that name. It registers once per page load. So re-rendering with a different
 * provider's configuration keeps the client the first one built, and `login()` signs the
 * person in against the wrong provider while every screen and every stored value says
 * otherwise. Measured: with `dhi_entra` remembered, going back and choosing MIKE Cloud
 * produced an authorize request to DHI's tenant, and `oidc.login.default` in storage still
 * held the Entra scope.
 *
 * `assign` rather than `replace`, so the screen being left stays in history and Back
 * returns to it, which is the whole point of the paths.
 *
 * The typed address rides along as `login_hint`, since the URL is the only thing that
 * survives the page load. The provider screen consumes it and hands it to the identity
 * provider, which is what saves a colleague typing it twice.
 *
 * It is also stored, which the URL cannot do for it: the parameter is consumed on arrival
 * and gone by the time the identity provider answers, and the answer is the thing that has
 * to be checked against it. See `auth/requestedAccount.ts` for why an address that only
 * picks a provider was a promise the sign-in could not keep.
 */
export function goToProviderScreen(
  provider: AuthProviderConfig,
  loginHint: string | null = null,
): void {
  const params = new URLSearchParams({ auto: '1' });
  if (loginHint) params.set('login_hint', loginHint);
  rememberRequestedAccount(loginHint);
  window.location.assign(`${signInProviderPath(provider.name)}?${params.toString()}`);
}

/** Leave for the email step by a full page load, for the same reasons. */
export function goToEmailStep(): void {
  window.location.assign(signInRootPath());
}

export type SignInDecision =
  | {
      kind: 'email';
      /**
       * The provider whose client to mount underneath the email step, so that a session
       * this browser already holds is found and honoured. Null when nothing is remembered,
       * which is also when there can be no session to find.
       */
      sessionProvider: AuthProviderConfig | null;
    }
  | {
      kind: 'gate';
      provider: AuthProviderConfig;
      /** Redirect to the identity provider at once rather than render a button. */
      autoSignIn: boolean;
      /**
       * Whether the gate may try the one silent sign-in on its own. Off on the provider
       * paths, where somebody has arrived by pressing Back to get away from the identity
       * provider, or by a link, and must be shown a button rather than sent straight
       * back. On everywhere else, `/signin` included: a remembered account there is a
       * returning person whose browser has forgotten the session, not a new visitor.
       */
      silentResume: boolean;
    };

export function decideSignIn(
  {
    pathname,
    providers,
    namedOnUrl,
    autoRequested,
    remembered,
  }: {
    pathname: string;
    providers: AuthProviderConfig[];
    /** The legacy `?provider=` link parameter, already consumed. */
    namedOnUrl: string | null;
    /** Whether `?auto=1` was on the URL, already consumed. */
    autoRequested: boolean;
    /** What `lastProvider.ts` remembers. */
    remembered: string | null;
  },
  base: string = import.meta.env.BASE_URL,
): SignInDecision {
  const byName = (name: string | null) =>
    name ? (providers.find((p) => p.name === name) ?? null) : null;
  const onSignInPath = isSignInPath(pathname, base);

  // One provider: nothing to choose, so no email step anywhere, and both paths simply
  // mount the gate. A link's intent to sign in is still honoured.
  if (providers.length === 1) {
    return {
      kind: 'gate',
      provider: providers[0],
      autoSignIn: autoRequested || byName(namedOnUrl) !== null,
      silentResume: providerNamedByPath(pathname, base) === null,
    };
  }

  // The URL first, in both of its forms. An unknown or stale name on a provider path is
  // not trusted: it falls through to the email step rather than mounting a gate against an
  // authority the backend will not validate.
  const fromPath = byName(providerNamedByPath(pathname, base));
  if (fromPath) {
    return { kind: 'gate', provider: fromPath, autoSignIn: autoRequested, silentResume: false };
  }
  const fromStorage = byName(remembered);
  if (onSignInPath) return { kind: 'email', sessionProvider: fromStorage };

  const fromLink = byName(namedOnUrl);
  if (fromLink) {
    return { kind: 'gate', provider: fromLink, autoSignIn: true, silentResume: false };
  }

  // Then storage, which is the ordinary returning case and why the email step is a
  // cold-start screen rather than a toll on every visit.
  if (fromStorage) {
    return { kind: 'gate', provider: fromStorage, autoSignIn: false, silentResume: true };
  }

  return { kind: 'email', sessionProvider: null };
}
